import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { inject, Injectable } from '@angular/core';
import {
  PreferencesSettingsKeyEnum,
} from '@monorepo-projects/alwayson-shared';
import {
  AuthorizationService,
  CommonUtils,
  NameValue,
  SettingsService,
} from '@infodata-group/inf-commons';
import { Tools } from '../engine/tools/tools';
import { BehaviorSubject, PartialObserver, Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SerializedCompanyPresetInterface } from '../interface/serialized/serialized-company-preset.interface';
import { CompanyPresetEvent } from '../engine/company-preset-event';
import { CompanyPreset } from '../model/preset/company-preset';
import { Folder } from '../model/preset/folder';
import { Preset } from '../model/preset/preset';
import { CompanyPresetEventTypeEnum } from '../enum/company-preset-event-type.enum';
import { ToolsPresetMigration } from '../engine/tools/tools-preset-migration';

const logger = getInfCLogger('PresetService');

@Injectable({
  providedIn: 'root',
})
export class PresetService {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly settingsService = inject(SettingsService);
  private readonly translateService = inject(TranslateService);

  private companyPresets: CompanyPreset[] = [];
  private currentCompanyPreset: CompanyPreset;
  private event$$ = new Subject<CompanyPresetEvent>();
  public datasourceFolders$$ = new BehaviorSubject<NameValue[]>([]);

  public init(): void {
    this.migratePreset();
    const settingsCompanyPresets = this.settingsService.getSetting(
      PreferencesSettingsKeyEnum.companyPresets,
    ) as SerializedCompanyPresetInterface[];
    if (!settingsCompanyPresets) {
      this.setDefaultCompanyPreset();
      return;
    }
    this.companyPresets = settingsCompanyPresets.map((scp) => {
      const companyPreset = new CompanyPreset(scp.id);
      companyPreset.createdAt = new Date(scp.createdAt);
      companyPreset.updatedAt = new Date(scp.updatedAt);
      companyPreset.folders = scp.folders.map(
        (f) =>
          new Folder(
            f.id,
            f.label,
            f.presets.map((p) => {
              const preset = new Preset(p.id, {
                label: p.label,
                folderId: f.id,
                bbx: p.selectedBbxId,
                bbxStatus: p.selectedStatusBbx,
                mats: p.selectedMaterialsId,
                sites: p.selectedSitesId,
                blackboxesDisplay: p.blackboxesDisplay,
                materialsDisplay: p.materialsDisplay,
                sitesDisplay: p.sitesDisplay,
              });
              preset.createdAt = new Date(p.createdAt);
              preset.updatedAt = new Date(p.updatedAt);
              preset.isNew = false;
              return preset;
            }),
          ),
      );
      return companyPreset;
    });
    this.currentCompanyPreset = this.companyPresets.find(
      (cp) => cp.id === Tools.getCurrentCompany(this.authorizationService),
    );
    if (!this.currentCompanyPreset) {
      this.setDefaultCompanyPreset();
      return;
    }
    this.event$$.next(
      new CompanyPresetEvent(
        CompanyPresetEventTypeEnum.currentCompanyPresetSelected,
        this.currentCompanyPreset,
      ),
    );
    this.datasourceFolders$$.next(
      this.currentCompanyPreset.folders.map((f) => f.toNameValue()),
    );
  }
  private migratePreset(): void {
    const currentCompany = Tools.getCurrentCompany(this.authorizationService);
    ToolsPresetMigration.migrateToCompanyPreset(
      currentCompany,
      this.settingsService,
    );
    ToolsPresetMigration.migrateToPresetV2(this.settingsService);
  }
  private setDefaultCompanyPreset(): void {
    const companyPreset = new CompanyPreset(
      Tools.getCurrentCompany(this.authorizationService),
      '',
    );
    const folder = new Folder(
      'default',
      this.translateService.instant('i18n.common.my-presets'),
    );
    const preset = new Preset('default', {
      label: this.translateService.instant('i18n.common.all'),
      folderId: 'default',
      blackboxesDisplay: 'all',
      materialsDisplay: 'all',
      sitesDisplay: 'all',
    });
    folder.addPreset(preset);
    companyPreset.folders = [folder];
    this.companyPresets.push(companyPreset);
    this.currentCompanyPreset = companyPreset;
    this.event$$.next(
      new CompanyPresetEvent(
        CompanyPresetEventTypeEnum.currentCompanyPresetSelected,
        this.currentCompanyPreset,
      ),
    );
    this.datasourceFolders$$.next(
      this.currentCompanyPreset.folders.map((f) => f.toNameValue()),
    );
  }
  public createFolder(folderName: string): Folder {
    const folder = new Folder(
      CommonUtils.normalize(folderName, true),
      folderName,
      [],
    );
    const existFolder = this.getFolderById(folder.getId());
    if (existFolder) {
      return existFolder;
    }
    this.currentCompanyPreset.folders.push(folder);
    this.datasourceFolders$$.next(
      this.getFolders().map((f) => f.toNameValue()),
    );
    return folder;
  }
  public getFolderById(folderId: string): Folder {
    return this.currentCompanyPreset.folders.find(
      (f) => f.getId() === folderId,
    );
  }
  public getFolders(): Folder[] {
    return this.currentCompanyPreset.folders;
  }
  public getPresetById(presetId: string): Preset {
    const preset = this.currentCompanyPreset.folders
      .map((f) => f.findPreset(presetId))
      .filter((p) => p !== undefined);
    if (preset.length > 0) {
      preset.at(0).isNew = false;
      return preset[0];
    }
    return undefined;
  }
  public addPreset(preset: Preset): boolean {
    const destFolder = this.currentCompanyPreset.folders.find(
      (f) => f.getId() === preset.folderId,
    );
    if (!destFolder) {
      logger.error(`Impossible to find folder with id "${preset.folderId}"`);
      return false;
    }
    this.currentCompanyPreset.folders.forEach((f) =>
      f.removePreset(preset.getId()),
    );
    destFolder.addPreset(preset);
    this.clearFolders();
    this._save();
    this.event$$.next(
      new CompanyPresetEvent(
        CompanyPresetEventTypeEnum.currentCompanyPresetChange,
        this.currentCompanyPreset,
      ),
    );
    return true;
  }
  public removePreset(preset: Preset): boolean {
    const folderIndex = this.currentCompanyPreset.folders.findIndex(
      (f) => f.id === preset.folderId,
    );
    if (folderIndex === -1) {
      return false;
    }
    this.currentCompanyPreset.folders[folderIndex].removePreset(preset.getId());
    this.clearFolders();
    this._save();
    this.event$$.next(
      new CompanyPresetEvent(
        CompanyPresetEventTypeEnum.currentCompanyPresetChange,
        this.currentCompanyPreset,
      ),
    );
    return true;
  }
  public clearFolders(): void {
    this.currentCompanyPreset.folders =
      this.currentCompanyPreset.folders.filter((f) => f.items.length > 0);
  }
  public subscribe(
    observer: PartialObserver<CompanyPresetEvent>,
  ): Subscription {
    return this.event$$.subscribe(observer);
  }
  private _save(): void {
    this.settingsService.addSetting(
      PreferencesSettingsKeyEnum.companyPresets,
      this.companyPresets.map((c) => c.serialize()),
    );
  }

  public clear() {
    this.companyPresets = [];
    this.currentCompanyPreset = null;
    this.datasourceFolders$$.next([]);
  }
}
